import { type Category, COUNTED_WAYS } from "./categories";
import { dec, toFixedTrimmed, toStorage, ZERO } from "./decimal";
import { type Dimension, toCanonical, UNITS, type UnitId } from "./units";

/**
 * The packs things come in, known ahead of any invoice.
 *
 * A pack used to be whatever the item said, or whatever the supplier printed: a lot received off a CMG
 * invoice was a "50LB/22.7K", off YCH a "1x5K/11L", and the same 5 kg box of hops was two different
 * things in the same list because two suppliers wrote it two ways. What a brewer counts is bags and
 * boxes (manifesto, Inventory Systems), so the pack is now one of a list of sizes this system knows,
 * and an invoice is read against it: its printed weight picks the pack, and the pack says what it is
 * called (Tyler, 2026-09-23).
 *
 * Two rules keep this honest.
 *
 * The weight stays the document's. A sack printed as 25 kg is received as 25 kg, not as the 55 lb it is
 * sold as; the known pack names it and lets it count alongside the 55 lb ones, and the quantity is
 * whatever the supplier billed (invariant 4 — nothing here rounds a figure to make it fit).
 *
 * Nothing is fitted to the nearest. A size within `FIT_TOLERANCE` of a known one is that pack, because
 * suppliers convert and round (55 lb is 24.95 kg and gets printed as 25; 50 lb is 22.68 and gets
 * printed as 22.7). A size further off than that is a new size, and the brewery names it once
 * (`BreweryPack`). Snapping to the closest is how a 22.7 kg bag would turn into a 25 kg one.
 */

export interface KnownPack {
  /** Singular noun a brewer counts in: "bag", "box", "brick". */
  name: string;
  dimension: Dimension;
  /** Canonical nominal size, as a decimal string. */
  size: string;
  /** How the size is said, in the unit it is sold by: "55 lb", "500 g". */
  says: string;
  /** What it is how things come in. Null for any category of the dimension. */
  categories: readonly Category[] | null;
}

/** Within 1% is the same pack. Distinct packs are much further apart: 50 and 55 lb bags are 10%. */
export const FIT_TOLERANCE = "0.01";

const known = (
  name: string,
  value: string,
  unit: UnitId,
  categories: readonly Category[] | null,
): KnownPack => {
  const dimension = UNITS[unit].dimension;
  return {
    name,
    dimension,
    size: toCanonical({ value, unit }, dimension),
    says: `${value} ${UNITS[unit].label}`,
    categories,
  };
};

/** Grain, sugar and the dry goods sold the way grain is. */
const SACKED: readonly Category[] = ["malt", "sugar", "adjunct", "brewing_aid", "chemical"];

/**
 * The built-in list. Taken from what White Oak's suppliers actually print and what the catalogs sell
 * (CMG, BSG's price list, RahrBSG, YCH), and deliberately short: a size nobody listed is named once
 * by the brewery that meets it, and that is cheaper than guessing at a size nobody sells.
 */
export const KNOWN_PACKS: readonly KnownPack[] = [
  // Malt comes in 55 lb (25 kg) and 50 lb (22.7 kg) bags, and so do the sugars and dry goods sold
  // alongside it.
  known("bag", "55", "lb", SACKED),
  known("bag", "50", "lb", SACKED),
  // Fruit puree comes in a 42 lb bag: Oregon Fruit's, at White Oak (Scott's Jam, 2026-10-02).
  known("bag", "42", "lb", ["adjunct"]),
  // Hops: an 11 lb (5 kg) box, and the 44 lb (20 kg) case of four.
  known("box", "11", "lb", ["hop"]),
  known("case", "44", "lb", ["hop"]),
  // Dry yeast: the 500 g brick, the 100 g pack, the 11.5 g sachet, and a 10 kg box.
  known("brick", "500", "g", ["yeast"]),
  known("pack", "100", "g", ["yeast"]),
  known("sachet", "11.5", "g", ["yeast"]),
  known("box", "10", "kg", ["yeast"]),
  // Liquids, whatever they are.
  known("jug", "1", "gal", null),
  known("pail", "5", "gal", null),
  known("drum", "55", "gal", null),
  known("tote", "275", "gal", null),
];

/**
 * A size a brewery has named for itself, because nothing on the list fitted it, or its own word for one
 * of the list's sizes in one kind of item: Five Star PBW's 50 lb is a bucket, not the bag 50 lb of malt
 * is (Scott's Jam, 2026-10-04). Settings > Packaging is where a brewery changes the list for itself.
 */
export interface BreweryPack {
  name: string;
  dimension: Dimension;
  size: string;
  says: string;
  category: Category | null;
  /**
   * Taken off for its kind in Settings: the kind does not come in this size, whatever the list says, so
   * a printed size of it is asked about rather than named.
   */
  removed?: boolean;
}

export interface FittedPack {
  name: string;
  /** The nominal size of the pack it fitted: what it is called, not what was weighed. */
  size: string;
  says: string;
  /** "55 lb bag". */
  label: string;
  /** Whose list it came from. */
  from: "known" | "brewery";
}

const labelOf = (pack: { says: string; name: string }) => `${pack.says} ${pack.name}`;

function within(size: string, nominal: string): boolean {
  const n = dec(nominal);
  if (n.lte(ZERO)) return false;
  return dec(size).minus(n).abs().div(n).lte(dec(FIT_TOLERANCE));
}

/** Whether a size is the pack of this nominal size: 25 kg is a 55 lb bag, 22.7 kg is not. */
export function sameSize(size: string, nominal: string): boolean {
  return within(size, nominal);
}

type Candidate = {
  pack: KnownPack | BreweryPack;
  from: FittedPack["from"];
  rank: number;
  removed: boolean;
};

/**
 * Every pack that could name a size in this context, with how strongly. The brewery's own word for its
 * kind first, because a brewery that has named a size has said what it calls it; then its word for any
 * kind; then the list's for this kind, then the list's for any. A size the kind took off outranks the
 * list the same way, and says it has no name.
 *
 * Where the kind is not known yet (an invoice line nobody has matched) the list comes before what one
 * kind calls a size, and a kind's removals do not apply: a 50 lb bucket of cleaner says nothing about a
 * 50 lb bag nobody has matched to anything.
 */
function candidatesFor(
  context: { dimension: Dimension; category?: Category | null },
  brewery: readonly BreweryPack[],
): Candidate[] {
  const out: Candidate[] = [];
  for (const pack of brewery) {
    if (pack.dimension !== context.dimension) continue;
    if (context.category) {
      if (pack.category && pack.category !== context.category) continue;
      out.push({ pack, from: "brewery", rank: pack.category ? 0 : 1, removed: !!pack.removed });
    } else if (!pack.removed) {
      out.push({ pack, from: "brewery", rank: pack.category ? 4 : 1, removed: false });
    }
  }
  for (const pack of KNOWN_PACKS) {
    if (pack.dimension !== context.dimension) continue;
    const listed = pack.categories === null || !context.category;
    if (!listed && !pack.categories?.includes(context.category as Category)) continue;
    out.push({ pack, from: "known", rank: pack.categories === null ? 3 : 2, removed: false });
  }
  return out;
}

const fitted = (c: Candidate): FittedPack => ({
  name: c.pack.name,
  size: c.pack.size,
  says: c.pack.says,
  label: labelOf(c.pack),
  from: c.from,
});

/** The strongest candidate for a size, taken off or not; the closer of two only between equals. */
function strongest(
  size: string,
  context: { dimension: Dimension; category?: Category | null },
  brewery: readonly BreweryPack[],
): Candidate | null {
  const distance = (p: { size: string }) => dec(size).minus(dec(p.size)).abs();
  const [best] = candidatesFor(context, brewery)
    .filter((c) => within(size, c.pack.size))
    .sort((a, b) => a.rank - b.rank || distance(a.pack).cmp(distance(b.pack)));
  return best ?? null;
}

/**
 * The pack a printed size is, or null when it is none of them, or its kind took that size off.
 *
 * Read in the light of the item's kind (see `candidatesFor`). Within 1% will not in practice find two
 * sizes, but where it does the closer is taken.
 */
export function fitPack(
  size: string,
  context: { dimension: Dimension; category?: Category | null },
  brewery: readonly BreweryPack[] = [],
): FittedPack | null {
  const best = strongest(size, context, brewery);
  if (best) return best.removed ? null : fitted(best);
  return context.dimension === "pitch" ? anyPitch(size) : null;
}

/**
 * A liquid pitch is grown for the batch it goes into (Omega props every order to the brewery's recipe,
 * 2026-10-07), so its size is never one off a list: any amount of pitching capacity is a pitch, said in
 * the barrels it pitches. A brewery that names a size for itself still has its own word for it.
 */
function anyPitch(size: string): FittedPack {
  const says = `${toFixedTrimmed(dec(size), 2)} ${UNITS.bbl_pitch.label}`;
  return { name: "pitch", size, says, label: `${says} pitch`, from: "known" };
}

/** One candidate per size, the strongest, taken off or not, in order of strength and then the list's. */
function oneEach(
  context: { dimension: Dimension; category?: Category | null },
  brewery: readonly BreweryPack[],
): Candidate[] {
  const kept: Candidate[] = [];
  for (const c of candidatesFor(context, brewery).sort((a, b) => a.rank - b.rank)) {
    if (kept.some((k) => within(c.pack.size, k.pack.size) || within(k.pack.size, c.pack.size))) {
      continue;
    }
    kept.push(c);
  }
  return kept;
}

/**
 * The packs a brewery can pick from for an item of this kind: its own first, then the list's. A size
 * appears once, under the name it is fitted to, and not at all where the kind took it off.
 */
export function packsFor(
  context: { dimension: Dimension; category?: Category | null },
  brewery: readonly BreweryPack[] = [],
): FittedPack[] {
  return oneEach(context, brewery)
    .filter((c) => !c.removed)
    .map(fitted);
}

export interface PackagingEntry {
  name: string;
  dimension: Dimension;
  size: string;
  says: string;
  label: string;
  /**
   * The list's; the brewery's own size; or one of the list's sizes under the brewery's word for it in
   * this kind, which Put back returns to the list's.
   */
  source: "built_in" | "own" | "renamed";
  /** What the list calls it, where the brewery renamed it. */
  listName: string | null;
}

/** What one kind of item comes in, as Settings > Packaging shows it. */
export interface Packaging {
  category: Category;
  packs: PackagingEntry[];
  /** The list's sizes this brewery took off for this kind, to put back. */
  removed: PackagingEntry[];
}

/**
 * The packs one kind comes in, in every way it is counted, largest first: what Settings > Packaging
 * shows, and what an invoice or a form for that kind is read against (Tyler, 2026-10-04).
 */
export function packagingOf(category: Category, brewery: readonly BreweryPack[]): Packaging {
  const packs: PackagingEntry[] = [];
  const removed: PackagingEntry[] = [];
  for (const dimension of COUNTED_WAYS[category]) {
    const context = { dimension, category };
    for (const c of oneEach(context, brewery)) {
      const entry = (p: { name: string; size: string; says: string }) => ({
        name: p.name,
        dimension,
        size: p.size,
        says: p.says,
        label: labelOf(p),
      });
      // This kind's own change to the list sits over what the size would be without it.
      const own = c.from === "brewery" && (c.pack as BreweryPack).category === category;
      const under = own
        ? strongest(
            c.pack.size,
            context,
            brewery.filter((p) => p !== c.pack),
          )
        : null;
      if (c.removed) {
        if (under && !under.removed) {
          removed.push({ ...entry(under.pack), source: "built_in", listName: null });
        }
      } else if (under && !under.removed) {
        packs.push({ ...entry(c.pack), source: "renamed", listName: under.pack.name });
      } else {
        packs.push({
          ...entry(c.pack),
          source: c.from === "known" ? "built_in" : "own",
          listName: null,
        });
      }
    }
  }
  const order = (a: PackagingEntry, b: PackagingEntry) =>
    COUNTED_WAYS[category].indexOf(a.dimension) - COUNTED_WAYS[category].indexOf(b.dimension) ||
    dec(b.size).cmp(dec(a.size));
  return { category, packs: packs.sort(order), removed: removed.sort(order) };
}

/**
 * What packs are called, offered wherever one is named; anything else can be typed. The words of the
 * UN/ECE list of package types that trade documents and e-invoices use (Recommendation 21), narrowed to
 * what a brewery receives and checked against what its suppliers print: a 60 lb pail of honey, a 640 lb
 * drum and a 3,000 lb tote of molasses, Oregon Fruit's 42 and 11 lb boxes (2026-10-04). "Bucket" and
 * "pail" are both there because brewers say one and suppliers print the other.
 */
export const PACK_TERMS = [
  "bag",
  "sack",
  "box",
  "case",
  "carton",
  "bucket",
  "pail",
  "jug",
  "bottle",
  "can",
  "drum",
  "keg",
  "tote",
  "tub",
  "jar",
  "pouch",
  "brick",
  "sachet",
] as const;

/**
 * The one pack every amount is a whole number of, or null when none is or several are: a report that
 * says only weight (Ollie's, 2026-10-02) has 100 lb of a malt, which is two 50 lb bags and no number
 * of 55 lb ones. Whole within the list's tolerance, since 50 kg written in pounds is 110.23. Amounts
 * and sizes are canonical. Opened stock is rarely whole, so this mostly finds nothing, and nothing is
 * the answer then rather than the nearest.
 */
export function packOfWholeAmounts<P extends { size: string }>(
  amounts: readonly string[],
  options: readonly P[],
): P | null {
  if (amounts.length === 0) return null;
  const whole = (amount: string, size: string) => {
    const n = dec(size);
    if (n.lte(ZERO) || dec(amount).lt(n.times(dec("1").minus(dec(FIT_TOLERANCE))))) return false;
    const packs = dec(amount).div(n);
    const nearest = packs.round();
    return packs.minus(nearest).abs().div(nearest).lte(dec(FIT_TOLERANCE));
  };
  const fitting = options.filter((o) => amounts.every((a) => whole(a, o.size)));
  // The same size twice (the brewery's name for a listed size) is still one answer.
  const sizes = new Set(fitting.map((o) => toStorage(dec(o.size))));
  return sizes.size === 1 ? (fitting[0] as P) : null;
}

/** "25 kg", "11.02 lb": a size said in the unit a person would, for a size nothing has named. */
export function saySize(size: string, unit: UnitId): string {
  const value = dec(size).div(dec(UNITS[unit].factor));
  return `${toFixedTrimmed(value, unit === "g" ? 1 : 2)} ${UNITS[unit].label}`;
}

/**
 * The size in one pack, from the shorthand suppliers print, in whichever way it measures: "55LB/25KG",
 * "50LB/22.7K", "1x5KG" and "500g" are weights; "1GAL", "20L" and "500ML" are volumes; "7 bbl
 * pitchable" is a liquid yeast pitch for 7 bbl of wort. Where two units are printed for the same pack
 * the first is taken, because it is the one the supplier priced against; nothing is inferred from a
 * label this does not recognise.
 *
 * Which way a size measures matters as much as the size: CMG prints "1GAL" against a fining agent,
 * and a gallon is a pack only of something counted by volume (2026-10-01).
 */
export function readPrintedSize(
  label: string,
): { dimension: "mass" | "volume" | "pitch"; size: string; unit: UnitId } | null {
  const text = label.trim();
  // A pitch is sold by the wort it pitches, not by what is in the bag (Omega, 2026-10-07).
  const pitch = /^(\d+(?:\.\d+)?)\s*BBL\s+PITCH(?:ABLE|ES)?\b/i.exec(text);
  if (pitch) return sized("pitch", pitch[1] as string, "bbl_pitch");
  const mass = (value: string, unit: UnitId) => sized("mass", value, unit);
  const pounds = /^(\d+(?:\.\d+)?)\s*LBS?\b/i.exec(text);
  if (pounds) return mass(pounds[1] as string, "lb");
  const kilos = /^(\d+(?:\.\d+)?)\s*KG?\b/i.exec(text);
  if (kilos) return mass(kilos[1] as string, "kg");
  // Grams, after kilos so "25 kg" is never read as a gram figure. Yeast is sold this way: BSG prints
  // "Fermentis SafLager W-34/70 500g", and the word boundary keeps "5 GAL" out.
  const grams = /^(\d+(?:\.\d+)?)\s*G\b/i.exec(text);
  if (grams) return mass(grams[1] as string, "g");
  // "1x5KG" is one box of 5 kg; "2x5KG" would be two.
  const multiple = /^(\d+)\s*x\s*(\d+(?:\.\d+)?)\s*(LBS?|KG?)\b/i.exec(text);
  if (multiple) {
    const unit = (multiple[3] as string).toLowerCase().startsWith("l") ? "lb" : "kg";
    const each = mass(multiple[2] as string, unit);
    if (!each) return null;
    return {
      dimension: "mass",
      size: toStorage(dec(each.size).times(dec(multiple[1] as string))),
      unit,
    };
  }
  const gallons = /^(\d+(?:\.\d+)?)\s*GAL(?:LONS?)?\b/i.exec(text);
  if (gallons) return sized("volume", gallons[1] as string, "gal");
  // Millilitres before litres; the word boundary keeps "LB" from reading as litres.
  const millilitres = /^(\d+(?:\.\d+)?)\s*ML\b/i.exec(text);
  if (millilitres) return sized("volume", millilitres[1] as string, "mL");
  const litres = /^(\d+(?:\.\d+)?)\s*(?:L|LTR|LITERS?|LITRES?)\b/i.exec(text);
  if (litres) return sized("volume", litres[1] as string, "L");
  return null;
}

/** A size in canonical units, with the unit it was printed in, which is how a person would say it. */
function sized(dimension: "mass" | "volume" | "pitch", value: string, unit: UnitId) {
  try {
    return { dimension, size: toCanonical({ value, unit }, dimension), unit };
  } catch {
    return null;
  }
}
